import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

import queryKeys from '@pray/shared/queryKeys';
import {
  getWeeklyReportUnsubscribe,
  postWeeklyReportUnsubscribe,
  type WeeklyReportUnsubscribeState,
} from '@pray/shared/services/studioService/superfunnelWeeklyReport';

import { Button } from 'components/ui/button';

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

function Shell({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <main className="flex min-h-screen items-start justify-center bg-secondary px-4 py-16 sm:py-24">
      <div className="w-full max-w-md rounded-xl border bg-background p-8">
        <p className="text-sm font-bold text-foreground">PRAY Studio</p>
        <h1 className="mt-8 text-balance text-2xl font-bold text-foreground">{title}</h1>
        <div className="mt-3 space-y-4 text-[15px] leading-relaxed text-muted-foreground">{children}</div>
      </div>
    </main>
  );
}

/**
 * Public page the SuperFunnel weekly report's "Stop these weekly reports" link opens. Opening it
 * changes nothing: mail scanners follow links in emails, so the unsubscribe happens only when the
 * person confirms. No login; the signed token is the credential.
 */
export default function WeeklyReportUnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const queryClient = useQueryClient();

  const link = useQuery({
    queryKey: queryKeys.weeklyReportUnsubscribe(token),
    queryFn: () => getWeeklyReportUnsubscribe(token),
    enabled: token !== '',
    retry: false,
    refetchOnWindowFocus: false,
  });

  const unsubscribe = useMutation({
    mutationFn: () => postWeeklyReportUnsubscribe(token),
    onSuccess: (state: WeeklyReportUnsubscribeState) =>
      queryClient.setQueryData(queryKeys.weeklyReportUnsubscribe(token), state),
  });

  // The link can stop being valid between reading it and confirming (the send deleted, say), so
  // the confirm's 400 and 404 mean the same as the read's.
  const linkStatus = statusOf(link.error) ?? statusOf(unsubscribe.error);

  if (token === '' || linkStatus === 400) {
    return (
      <Shell title="This unsubscribe link isn’t valid">
        <p>Use the link at the bottom of your latest weekly report email, and make sure it wasn’t cut short.</p>
      </Shell>
    );
  }

  if (linkStatus === 404) {
    return (
      <Shell title="We couldn’t find that email">
        <p>The weekly report this link came from no longer exists. Use the link in a more recent report.</p>
      </Shell>
    );
  }

  if (link.isError) {
    return (
      <Shell title="We couldn’t load this page">
        <p>Something went wrong on our side. Your settings haven’t changed.</p>
        <Button variant="outline" onClick={() => link.refetch()}>
          Try again
        </Button>
      </Shell>
    );
  }

  if (!link.data) {
    return (
      <Shell title="Checking your link…">
        <p aria-live="polite">One moment.</p>
      </Shell>
    );
  }

  const { ministry_name: ministryName, email_hint: emailHint, unsubscribed } = link.data;

  if (unsubscribed) {
    return (
      <Shell title="You’re unsubscribed">
        <p>
          <span className="font-medium text-foreground">{emailHint}</span> won’t get the SuperFunnel weekly report for{' '}
          <span className="font-medium text-foreground">{ministryName}</span> anymore.
        </p>
        <p>Other emails from PRAY Studio aren’t affected.</p>
      </Shell>
    );
  }

  return (
    <Shell title="Stop the weekly report?">
      <p>
        <span className="font-medium text-foreground">{emailHint}</span> gets the SuperFunnel weekly report for{' '}
        <span className="font-medium text-foreground">{ministryName}</span> every week.
      </p>
      <p>Other emails from PRAY Studio aren’t affected.</p>
      {unsubscribe.isError && (
        <p role="alert" className="text-destructive">
          We couldn’t unsubscribe you. Try again.
        </p>
      )}
      <Button className="w-full sm:w-auto" disabled={unsubscribe.isPending} onClick={() => unsubscribe.mutate()}>
        {unsubscribe.isPending ? 'Stopping…' : 'Stop weekly reports'}
      </Button>
    </Shell>
  );
}
